import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import '../../assets/styles/WxLogin.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

const WxLogin = () => {
    const [verifyCode, setVerifyCode] = useState(null);
    const [sceneId, setSceneId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [timeLeft, setTimeLeft] = useState(300); // 5分钟 = 300秒
    const wsRef = useRef(null);
    const timerRef = useRef(null);
    const history = useHistory();

    // 获取验证码
    const getVerifyCode = async () => {
        setIsLoading(true);
        try {
            const data = await api.fetchWithToken('/wx/login/getVerifyCode', {
                method: 'GET'
            });

            if (data.code !== 200) {
                toast.error(data.message || '获取验证码失败');
                return;
            }

            setVerifyCode(data.data.verifyCode);
            setSceneId(data.data.sceneId);
            setTimeLeft(300); // 重置计时器

            // 关闭之前的WebSocket连接
            if (wsRef.current) {
                wsRef.current.close();
            }

            // 建立新的WebSocket连接
            initWebSocket(data.data.sceneId);
        } catch (error) {
            toast.error('网络请求出错，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    };

    // 初始化WebSocket连接
    const initWebSocket = (sid) => {
        // 获取API基础URL
        const API_BASE_URL = process.env.NODE_ENV === 'production' ? 'ws://devsflow.cn' : 'ws://localhost:7447';
        const wsUrl = `${API_BASE_URL}/ws/wx/login?sceneId=${sid}`;

        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
            // WebSocket连接已建立
        };

        ws.onmessage = async (event) => {
            const token = event.data;

            // 存储token
            localStorage.setItem('satoken', token);

            // 登录成功后立即获取用户信息
            try {
                const userInfoResponse = await api.fetchWithToken('/user/getUserInfo', {
                    method: 'GET'
                });

                if (userInfoResponse?.success && userInfoResponse.data) {
                    // 处理头像URL，去除可能的反引号
                    const avatarUrl = userInfoResponse.data.avatarUrl ?
                        userInfoResponse.data.avatarUrl.trim().replace(/`/g, '') : '';

                    // 缓存头像URL
                    if (avatarUrl) {
                        // 导入cacheAvatar函数
                        const { cacheAvatar } = await import('../../utils/cacheUtils');
                        cacheAvatar(avatarUrl);
                    }

                    // 存储用户信息到localStorage，供聊天系统使用
                    const userInfo = {
                        id: userInfoResponse.data.id,
                        nickname: userInfoResponse.data.nickName || userInfoResponse.data.userAccount || '用户',
                        avatar: avatarUrl || '',
                        userAccount: userInfoResponse.data.userAccount,
                        // 保留其他可能需要的字段
                        ...userInfoResponse.data
                    };
                    localStorage.setItem('userInfo', JSON.stringify(userInfo));

                    // 检查是否需要设置账号
                    if (!userInfoResponse.data.userAccount) {
                        // 设置一个标志，表示用户需要设置账号
                        localStorage.setItem('needSetAccount', 'true');
                        // 显示登录成功提示
                        toast.success('登录成功！');
                        // 跳转到首页，首页会检测标志并显示设置账号弹窗
                        history.push('/home');
                        return;
                    }
                }

                // 显示普通登录成功提示
                toast.success('登录成功！');

                // 跳转到首页
                history.push('/home');
            } catch (error) {
                // 即使获取用户信息失败，也允许用户登录
                toast.success('登录成功！');
                history.push('/home');
            }
        };

        ws.onerror = (error) => {
            toast.error('连接出错，请刷新验证码重试');
        };

        ws.onclose = () => {
            // WebSocket连接已关闭
        };

        wsRef.current = ws;
    };

    // 组件挂载时获取验证码，只执行一次
    useEffect(() => {
        getVerifyCode();

        // 组件卸载时清理
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []); // 空依赖数组确保只执行一次

    // 设置倒计时
    useEffect(() => {
        if (verifyCode) {
            // 清除之前的计时器
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }

            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        // 时间到，自动刷新验证码
                        clearInterval(timerRef.current);
                        getVerifyCode();
                        return 300;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [verifyCode]); // 只在verifyCode变化时执行

    // 格式化时间
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    return (
        <div className="wx-login-container">
            <h2>微信扫码登录</h2>

            <div className="wx-login-content">
                <div className="qrcode-container">
                    <img src="/images/qrcode.jpg" alt="微信公众号二维码" />
                    <p className="qrcode-tip">微信扫码，关注我们</p>
                </div>

                <div className="verify-code-container">
                    {isLoading ? (
                        <div className="loading-animation">加载中...</div>
                    ) : (
                        <>
                            <div className="verify-code">
                                {verifyCode && String(verifyCode).split('').map((digit, index) => (
                                    <span key={index} className="verify-digit">{digit}</span>
                                ))}
                            </div>
                            <div className="verify-actions">
                                <button
                                    className="refresh-button"
                                    onClick={getVerifyCode}
                                    disabled={isLoading}
                                >
                                    <FontAwesomeIcon icon={faSync} spin={isLoading} />
                                </button>
                                <p className="verify-status">
                                    验证码有效期: {formatTime(timeLeft)}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <p className="operation-guide">
                请使用微信扫描二维码，关注公众号后，发送上方4位数字即可登录
            </p>
        </div>
    );
};

export default WxLogin;